import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import Logo from './Logo';
import { Button } from '@/components/ui/button';
import { Menu, X, LayoutDashboard, LogOut, User, Settings, ChevronDown, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = ({ variant = 'marketing' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const marketingLinks = [
    { href: '/pricing', labelKey: 'nav_pricing' },
    { href: '/faq', labelKey: 'nav_faq' },
    { href: '/contact', labelKey: 'nav_contact' },
  ];

  // App navbar for authenticated users
  if (variant === 'app') {
    return (
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" data-testid="nav-logo">
                <Logo />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {user?.is_admin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" data-testid="nav-admin-btn">
                    Admin
                  </Button>
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2" data-testid="nav-user-menu">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:block font-medium">{user?.name}</span>
                    <ChevronDown size={16} className="text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/market-data')} data-testid="menu-market-data">
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Market Data
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contacts')} data-testid="menu-contacts">
                    <User className="mr-2 h-4 w-4" />
                    Contacts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="menu-account">
                    <Settings className="mr-2 h-4 w-4" />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Marketing navbar
  const navBg = scrolled || !isHomePage 
    ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm' 
    : 'bg-transparent';
  
  const textColor = scrolled || !isHomePage ? 'text-foreground' : 'text-white';
  const linkColor = scrolled || !isHomePage ? 'text-muted-foreground hover:text-foreground' : 'text-white/80 hover:text-white';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" data-testid="nav-logo">
              <Logo variant={scrolled || !isHomePage ? 'dark' : 'light'} />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {marketingLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? (scrolled || !isHomePage ? 'text-blue-600' : 'text-white')
                    : linkColor
                }`}
                data-testid={`nav-${link.labelKey.split('_')[1]}`}
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'EN' ? 'ES' : 'EN')}
              className={`flex items-center gap-2 font-semibold ${scrolled || !isHomePage ? 'text-foreground hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
              data-testid="language-switcher"
            >
              <Globe size={18} />
              {language === 'EN' ? 'ES' : 'EN'}
            </Button>
            
            {user ? (
              <Link to="/dashboard">
                <Button 
                  className="btn-primary-gradient text-white rounded-full border-0" 
                  data-testid="nav-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className={scrolled || !isHomePage ? '' : 'text-white hover:bg-white/10'}
                    data-testid="nav-login-btn"
                  >
                    {t('nav_login')}
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button 
                    className="btn-primary-gradient text-white rounded-full border-0" 
                    data-testid="nav-signup-btn"
                  >
                    {t('nav_signup')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 rounded-lg ${scrolled || !isHomePage ? 'text-foreground' : 'text-white'}`}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {/* Language Switcher - Mobile */}
            <Button
              variant="outline"
              onClick={() => setLanguage(language === 'EN' ? 'ES' : 'EN')}
              className="w-full flex items-center justify-center gap-2 font-semibold rounded-full"
              data-testid="language-switcher-mobile"
            >
              <Globe size={18} />
              {language === 'EN' ? 'Cambiar a Español' : 'Switch to English'}
            </Button>
            
            {marketingLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block py-2 text-base font-medium text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-100 space-y-3">
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full btn-primary-gradient text-white rounded-full border-0">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full">
                      {t('nav_login')}
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full btn-primary-gradient text-white rounded-full border-0">
                      {t('nav_signup')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
